import Bugsnag from '@bugsnag/js'
import { Alert, Button, Divider, FormControlLabel, Grid, Skeleton, Switch, Typography, useTheme } from '@mui/material'
import { Dispatch, SetStateAction, useEffect, useRef, useState } from 'react'
import { MdNavigateNext, MdOutlineZoomIn } from 'react-icons/md'
import { useLocation, useNavigate } from 'react-router-dom'
import { Formtitle, Layout, ModalContract } from '../components'
import { client } from '../utils/client'
import { loadCpfDocumentHtml } from '../utils/pdfToHtml'
import { FormType } from '../utils/sharedTypes'

type Resp = { program_url: string; internal_rules_url: string; required: boolean; signed: boolean }
type Kind = 'program' | 'internal_rules'

const emptyModal = { open: false, html_document: '', has_button: false }

const CPFSignature = ({ form, setForm }: { form: FormType; setForm: Dispatch<SetStateAction<FormType>> }) => {
  const theme = useTheme()
  const navigate = useNavigate()
  const { search, state } = useLocation()
  const [docs, setDocs] = useState<Resp | null>(null)
  const [pages, setPages] = useState<Partial<Record<Kind, string>>>({})
  const [read, setRead] = useState<Record<Kind, boolean>>({ program: false, internal_rules: false })
  const [openKind, setOpenKind] = useState<Kind | null>(null)
  const [modal, setModal] = useState(emptyModal)
  const [error, setError] = useState<string | null>(null)
  const [loadAttempt, setLoadAttempt] = useState(0)
  const signingRef = useRef(false)
  const wentNext = useRef(false)
  const url = `/registration/${form.data.global_id}/cpf_documents_signature/`

  const goToRecap = (signature: { required: boolean; signed: boolean }) => {
    setForm((prev) => ({
      ...prev,
      data: { ...prev.data, cpf_documents_signature: signature },
    }))
    navigate({ pathname: './../cpf/', search })
  }

  useEffect(() => {
    if (!form.data.global_id) return
    let cancelled = false
    const globalId = form.data.global_id
    setError(null)
    client
      .get(url)
      .then(async (resp) => {
        if (cancelled) return
        const data = resp.data as Resp
        setForm((prev) => ({
          ...prev,
          data: {
            ...prev.data,
            cpf_documents_signature: { required: data.required, signed: data.signed },
          },
        }))
        const [program, internalRules] = await Promise.all([
          loadCpfDocumentHtml(globalId, 'program'),
          loadCpfDocumentHtml(globalId, 'internal_rules'),
        ])
        if (cancelled) return
        if (data.signed) setRead({ program: true, internal_rules: true })
        setDocs(data)
        setPages({ program, internal_rules: internalRules })
      })
      .catch((err: Error) => {
        Bugsnag.notify(err)
        if (!cancelled) setError('Impossible de charger les documents à signer.')
      })
    return () => {
      cancelled = true
    }
  }, [form.data.global_id, loadAttempt])

  useEffect(() => {
    if ((state as { cpfSignatureError?: boolean } | null)?.cpfSignatureError) {
      setError("La signature n'a pas pu être enregistrée.")
    }
  }, [state])

  const backToSignature = () => {
    setForm((prev) => ({
      ...prev,
      data: {
        ...prev.data,
        cpf_documents_signature: {
          required: prev.data.cpf_documents_signature?.required ?? true,
          signed: false,
        },
      },
    }))
    navigate({ pathname: './../cpf-signature/', search }, { state: { cpfSignatureError: true } })
  }

  // Le POST ne change pas de page. Sans cette recopie, Suivant relit
  // encore signed=false et relance un second tampon API2PDF.
  const rememberSigned = (signature: Pick<Resp, 'required' | 'signed'>) => {
    if (!signature.signed) return
    setDocs((prev) => (prev ? { ...prev, required: signature.required, signed: true } : prev))
    setForm((prev) => ({
      ...prev,
      data: {
        ...prev.data,
        cpf_documents_signature: { required: signature.required, signed: true },
      },
    }))
  }

  const sign = () => {
    if (signingRef.current) return
    signingRef.current = true
    setError(null)
    // Le POST tamponne les PDF (API2PDF). Il ne change pas de page :
    // le passage au récapitulatif se fait au clic sur Suivant.
    client
      .post(url, undefined, { timeout: 120_000 })
      .then(async (resp) => {
        const data = resp.data as Resp
        if (data.signed) {
          rememberSigned(data)
          return
        }
        const check = await client.get<Resp>(url)
        if (check.data.signed) {
          rememberSigned(check.data)
          return
        }
        if (wentNext.current) backToSignature()
        else setError("La signature n'a pas pu être enregistrée.")
      })
      .catch(async (err: Error) => {
        try {
          const resp = await client.get<Resp>(url)
          if (resp.data.signed) {
            rememberSigned(resp.data)
            return
          }
        } catch {
          // conserve l'erreur d'origine
        }
        Bugsnag.notify(err)
        if (wentNext.current) backToSignature()
        else setError("La signature n'a pas pu être enregistrée.")
      })
      .finally(() => {
        signingRef.current = false
      })
  }

  const goNext = () => {
    wentNext.current = true
    if (!form.data.cpf_documents_signature?.signed) sign()
    goToRecap({
      required: form.data.cpf_documents_signature?.required ?? true,
      signed: true,
    })
  }

  const openDocument = (kind: Kind) => {
    const html = pages[kind]
    if (!html) return
    setOpenKind(kind)
    setModal({ open: true, html_document: html, has_button: !(docs?.signed || read[kind]) })
  }

  const approveOpenDocument = () => {
    if (!openKind) return
    const next = { ...read, [openKind]: true }
    setRead(next)
    setModal(emptyModal)
    setOpenKind(null)
    if (next.program && next.internal_rules && !docs?.signed) sign()
  }

  const card = (kind: Kind, title: string, caption: string) => {
    const approved = Boolean(docs?.signed || read[kind])
    return (
      <Grid key={kind}>
        <Grid
          sx={{
            width: '100%',
            height: '300px',
            mt: 2,
            py: 6,
            px: 10,
            borderRadius: '4px 4px 0 0',
            background: 'rgba(0,0,0,0.54)',
            overflow: 'hidden',
            position: 'relative',
            cursor: pages[kind] ? 'pointer' : 'default',
          }}
          onClick={() => openDocument(kind)}
        >
          <Grid
            container
            justifyContent="center"
            alignItems="center"
            sx={{
              top: 0,
              left: 0,
              width: '100%',
              height: '100%',
              background: 'rgba(0,0,0,0.54)',
              position: 'absolute',
            }}
          >
            <Grid
              container
              justifyContent="center"
              alignItems="center"
              sx={{ width: 60, height: 60, borderRadius: '4px', background: 'white' }}
            >
              <MdOutlineZoomIn size="30px" />
            </Grid>
          </Grid>
          <Grid
            sx={{ background: 'white', borderRadius: '4px', py: 2, px: 4 }}
            dangerouslySetInnerHTML={{ __html: pages[kind]?.replace(/margin/g, '') ?? '' }}
          />
        </Grid>
        <Grid
          container
          flexWrap="nowrap"
          sx={{
            p: 2,
            background: approved ? `${theme.palette.success.main}1A` : 'transparent',
            border: approved ? `1px solid ${theme.palette.success.main}1A` : '1px solid rgba(0,0,0,0.12)',
            borderRadius: '0 0 4px 4px',
            cursor: 'pointer',
            flexDirection: { md: 'row', xs: 'column' },
          }}
          onClick={() => openDocument(kind)}
        >
          <Grid container direction="column">
            <Typography variant="body1" sx={{ fontWeight: 500 }}>
              {title}
            </Typography>
            <Typography variant="caption">{pages[kind] ? caption : 'Chargement du document…'}</Typography>
          </Grid>
          <Divider
            sx={{
              width: { md: 0, xs: '100%' },
              height: { md: 0, xs: '1px' },
              my: { md: 0, xs: 2 },
            }}
          />
          <Grid item container justifyContent="end">
            <FormControlLabel
              required
              control={<Switch color="success" disabled checked={approved} />}
              label={
                <Typography variant="body1" sx={{ fontWeight: approved ? 500 : 400 }}>
                  Lu et approuvé
                </Typography>
              }
            />
          </Grid>
        </Grid>
      </Grid>
    )
  }

  return (
    <Layout form={form.data} steps={form.steps}>
      <Formtitle title="Signature" />
      <ModalContract
        modal={modal}
        setModal={setModal}
        isSigningUp={approveOpenDocument}
        signLabel="Lu et approuvé"
        maxWidth={800}
        flush
        signHint="Vous devez parcourir le document jusqu’à la fin afin de pouvoir valider sa lecture. Une fois à la fin, cliquez sur « Lu et approuvé »."
      />
      {!docs && !error &&
        ['program', 'internal_rules'].map((kind) => (
          <Grid key={kind}>
            <Skeleton variant="rectangular" height={300} sx={{ mt: 2, borderRadius: '4px 4px 0 0' }} />
            <Grid
              container
              alignItems="center"
              flexWrap="nowrap"
              sx={{ p: 2, gap: 2, border: '1px solid rgba(0,0,0,0.12)', borderRadius: '0 0 4px 4px' }}
            >
              <Grid container direction="column" sx={{ flex: 1 }}>
                <Skeleton variant="text" width="55%" sx={{ fontSize: '1rem' }} />
                <Skeleton variant="text" width="80%" sx={{ fontSize: '0.75rem' }} />
              </Grid>
              <Skeleton variant="rounded" width={140} height={28} />
            </Grid>
          </Grid>
        ))}
      {error && (
        <Alert
          severity="error"
          sx={{ mt: 2 }}
          action={
            <Button
              color="inherit"
              size="small"
              onClick={() => {
                if (read.program && read.internal_rules) sign()
                else setLoadAttempt((attempt) => attempt + 1)
              }}
            >
              Réessayer
            </Button>
          }
        >
          {error}
        </Alert>
      )}
      {docs && (
        <>
          {card('program', 'Signez le programme de formation', 'Je lis et approuve le programme de formation')}
          {card(
            'internal_rules',
            'Signez le règlement intérieur',
            'Applicable aux actions de formation et aux actions de formation par apprentissage',
          )}
          <Grid container justifyContent="flex-end" sx={{ mt: 2 }}>
            <Button
              variant="contained"
              disabled={!(read.program && read.internal_rules)}
              sx={{ height: { xs: 45 } }}
              endIcon={<MdNavigateNext />}
              onClick={goNext}
            >
              Suivant
            </Button>
          </Grid>
        </>
      )}
    </Layout>
  )
}

export default CPFSignature
